import { faBold } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { InputRule } from "prosemirror-inputrules";
import { MarkType } from "prosemirror-model";

import { toggleMark } from "~/lib/prose/commands";

import { type KeysArgs, MarkExtension } from "../extension";
import { markActive } from "../utils";

const boldRegex = /(?:^|[^*a-zA-Z\d])\*([^\s*](?:[^*\v]|\*\s)*[^\s*]|[^\s*])\*([^\s*]|$)$/g;

export default class BoldMark extends MarkExtension {
	get name() {
		return "bold";
	}

	get mark() {
		return {
			parseDOM: [
				{ tag: "strong" },
				{ tag: "b", getAttrs: (node: HTMLElement) => node.style.fontWeight != "normal" && null },
				{ style: "font-weight", getAttrs: (value: string) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null },
			],
			toDOM() {
				return ["strong", 0];
			},
		};
	}

	keys({ $type }: KeysArgs) {
		return {
			"Mod-b": toggleMark($type as MarkType, {}),
		};
	}

	inputRules() {
		return [
			new InputRule(boldRegex, (state, match, start, end) => {
				const boldMark = state.schema.mark(this.name);
				const codeMark = state.schema.mark("code");

				// Don't apply the mark if a code mark is active.
				// This makes it so you can use the backtick in code
				if (markActive(codeMark)(state)) return null;

				// The regex may match a non-word character ahead of the opening asterisk to assure that there
				// 	is not a second asterisk. This character then appears in match[0] though it's unneeded
				const prefixSymbolCompensation = !match[0].startsWith("*") ? 1 : 0;

				return state.tr
					.insertText("", start + prefixSymbolCompensation, start + 1 + prefixSymbolCompensation)
					.insertText("", end, end)
					.addMark(start + prefixSymbolCompensation, end - 1, boldMark)
					.removeStoredMark(boldMark);
			}),
		];
	}

	menuEntry() {
		return {
			icon: faBold,
			title: "Bold",
			run: () => toggleMark(this.$type, {}),
			section: "formatting" as const,
			group: "basic",
			priority: 5,
			keyboardShortcut: this.cmdPlus("B"),
		};
	}
}
